import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi, urlApi } from './api';
import type { EtatTest } from './useTestsPositionnement';

// ---------------------------------------------------------------------------
// Test EVA de repérage de l'illettrisme, côté pédagogique (TF-441). Passé hors
// de la plateforme, il est saisi au dossier par le pôle pédagogique, compte
// rendu joint. L'API dit quel passage fait foi et l'accompagnement conseillé :
// l'écran ne recalcule rien.
// ---------------------------------------------------------------------------

export type TonEva = 'danger' | 'warning' | 'success';

export interface AccompagnementEva {
  niveau: 'RENFORCE' | 'ATELIER_ECRIT' | 'STANDARD';
  accompagnement: string;
  reperage: string;
  ton: TonEva;
}

export interface ResultatEva {
  id: string;
  score: number;
  scoreSur: number;
  passeLe: string;
  commentaire: string | null;
  compteRendu: { nomFichier: string; typeMime: string; tailleOctets: number } | null;
  saisiPar: string | null;
  saisiLe: string;
  accompagnement: AccompagnementEva;
  faitFoi: boolean;
}

export interface TestDuDossier {
  id: string;
  titre: string;
  niveau: string | null;
  score: number | null;
  scoreSur: number;
  etat: EtatTest;
  etatLibelle: string;
  etatTon: string;
  attribueLe: string;
  soumisLe: string | null;
  corrigeLe: string | null;
}

export interface PositionnementDossier {
  apprenantId: string;
  eva: ResultatEva | null;
  historique: ResultatEva[];
  tests: TestDuDossier[];
  formatsCompteRendu: string;
}

/** Résultat qui fait foi, lu à côté d'un test de positionnement. */
export interface EvaEnBref {
  score: number;
  scoreSur: number;
  passeLe: string;
  reperage: string;
  ton: TonEva;
}

export interface SaisieEva {
  score: string;
  passeLe: string;
  commentaire: string;
  fichier: File | null;
  retirerCompteRendu: boolean;
}

function formulaireEva(saisie: SaisieEva): FormData {
  const formulaire = new FormData();
  formulaire.append('score', saisie.score.trim());
  formulaire.append('passeLe', saisie.passeLe);
  if (saisie.commentaire.trim()) formulaire.append('commentaire', saisie.commentaire.trim());
  if (saisie.fichier) formulaire.append('fichier', saisie.fichier);
  if (saisie.retirerCompteRendu) formulaire.append('retirerCompteRendu', 'true');
  return formulaire;
}

function cleDossier(apprenantId: string): string[] {
  return ['eva-dossier', apprenantId];
}

function apresEcriture(client: QueryClient, positionnement: PositionnementDossier): void {
  client.setQueryData(cleDossier(positionnement.apprenantId), positionnement);
  void client.invalidateQueries({ queryKey: ['tests-positionnement'] });
}

export function urlCompteRenduEva(resultatId: string): string {
  return urlApi(`/pedagogique/eva/resultats/${encodeURIComponent(resultatId)}/compte-rendu`);
}

export function usePositionnementDossier(apprenantId: string, actif: boolean) {
  return useQuery({
    queryKey: cleDossier(apprenantId),
    queryFn: () =>
      appelApi<PositionnementDossier>(`/pedagogique/eva/dossiers/${encodeURIComponent(apprenantId)}`),
    enabled: actif && apprenantId.length > 0,
  });
}

export function useSaisirEva(apprenantId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieEva) =>
      appelApi<PositionnementDossier>(`/pedagogique/eva/dossiers/${encodeURIComponent(apprenantId)}`, {
        methode: 'POST',
        formulaire: formulaireEva(saisie),
      }),
    onSuccess: (positionnement) => apresEcriture(client, positionnement),
  });
}

export function useCorrigerEva() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ resultatId, saisie }: { resultatId: string; saisie: SaisieEva }) =>
      appelApi<PositionnementDossier>(`/pedagogique/eva/resultats/${encodeURIComponent(resultatId)}`, {
        methode: 'PUT',
        formulaire: formulaireEva(saisie),
      }),
    onSuccess: (positionnement) => apresEcriture(client, positionnement),
  });
}

export function useRetirerEva() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (resultatId: string) =>
      appelApi<PositionnementDossier>(`/pedagogique/eva/resultats/${encodeURIComponent(resultatId)}`, {
        methode: 'DELETE',
      }),
    onSuccess: (positionnement) => apresEcriture(client, positionnement),
  });
}
